import { FileUploader, FileUploaderConfig } from './FileUploader.js';

/**
 * お問い合わせフォーム用の画像アップローダー
 */
export class ContactImageUploader extends FileUploader {

  constructor(element: HTMLElement) {
    // data-name属性からinput nameを取得（動的に対応）
    const fileInput = element.querySelector('input[type="file"]') as HTMLInputElement;
    const fileInputName = fileInput?.name || 'contact_images[]';

    const config: FileUploaderConfig = {
      fileInputName: fileInputName,
      nonceName: 'contact_form_nonce',
      uploadAction: 'contact_image_upload',
      deleteAction: 'contact_image_delete',
      acceptedFileTypes: '.jpg,.jpeg,.png,.gif,.webp',
      maxFileSize: 2097152, // 2MB (2 * 1024 * 1024)
      messages: {
        deleteConfirm: 'アップロードした画像を削除しますか？'
      }
    };

    super(element, config);

    // 初期化時に既にファイルが選択されている場合の処理
    this.checkInitialFileState();
  }

  /**
   * 初期化時のファイル状態をチェック
   */
  private checkInitialFileState(): void {
    const placeholderElement = this.container.querySelector('.js-file-placeholder');

    if (placeholderElement &&
        placeholderElement.textContent &&
        placeholderElement.textContent.trim() !== '' &&
        placeholderElement.textContent !== '選択されていません') {
      // ファイル名が既に表示されている場合、削除ボタンを表示
      const actionsElement = this.container.querySelector('.js-file-upload-actions');
      if (actionsElement) {
        actionsElement.classList.add('is-visible');
      }
    }
  }

  /**
   * ファイル選択時の処理をオーバーライド（Ajaxアップロードをスキップ）
   */
  protected handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('Image file selected:', file.name);
      console.log('File size:', file.size);
      console.log('File type:', file.type);

      // ファイルサイズチェック（2MB）
      const maxSize = this.config.maxFileSize || 2097152;
      if (file.size > maxSize) {
        alert(`ファイルサイズが大きすぎます。1枚あたり最大${maxSize / 1024 / 1024}MBまでです。`);
        input.value = '';
        return;
      }

      // ファイル形式チェック
      const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!acceptedTypes.includes(file.type)) {
        alert('画像ファイル（JPG, PNG, GIF, WebP）のみアップロードできます。');
        input.value = '';
        return;
      }

      // Ajaxアップロードせずに、すぐにファイル名を表示
      this.updateFileDisplay(file.name);
    }
  }

  /**
   * ファイル表示を更新
   */
  protected updateFileDisplay(fileName: string): void {
    // placeholderにファイル名を設定
    const placeholderElement = this.container.querySelector('.js-file-placeholder');
    if (placeholderElement) {
      placeholderElement.textContent = fileName;
    }

    // 削除ボタンを表示
    const actionsElement = this.container.querySelector('.js-file-upload-actions');
    if (actionsElement) {
      actionsElement.classList.add('is-visible');
    }

    // イベントリスナーを再設定
    this.bindAllEventListeners();
  }

  /**
   * ファイル削除処理をオーバーライド（Ajaxリクエストをスキップ、削除フラグ設定）
   */
  protected handleDelete(): void {
    console.log('ContactImageUploader: handleDelete called - resetting file display');
    // 確認なしで即座にリセット（セッション管理の場合）
    this.resetFileDisplay();
  }

  /**
   * ファイル表示を通常の選択状態にリセット
   */
  protected resetFileDisplay(): void {
    // placeholderを初期状態に戻す
    const placeholderElement = this.container.querySelector('.js-file-placeholder');
    if (placeholderElement) {
      placeholderElement.textContent = '選択されていません';
    }

    // 削除ボタンを非表示
    const actionsElement = this.container.querySelector('.js-file-upload-actions');
    if (actionsElement) {
      actionsElement.classList.remove('is-visible');
    }

    // 全てのファイル入力をクリア
    const fileInputs = this.container.querySelectorAll(`input[name="${this.config.fileInputName}"]`);
    fileInputs.forEach(input => {
      (input as HTMLInputElement).value = '';
    });

    // 削除フラグを1に設定
    const deleteFlag = this.container.querySelector('.js-delete-flag') as HTMLInputElement;
    if (deleteFlag) {
      deleteFlag.value = '1';
      console.log('Delete flag set to 1 for:', deleteFlag.name);
    } else {
      console.warn('Delete flag not found in container');
    }

    // イベントリスナーを再設定
    this.bindAllEventListeners();
  }
}

// DOMContentLoadedイベントで初期化（ContactFileUploaderより先に実行されるように）
document.addEventListener("DOMContentLoaded", () => {
  const imageUploaderElements = document.querySelectorAll(".js-contact-file-uploader");
  imageUploaderElements.forEach((element) => {
    // input[type="file"]のname属性をチェックして、画像アップロード用か判定
    const fileInput = element.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput && fileInput.name.includes('contact_images')) {
      new ContactImageUploader(element as HTMLElement);
      // 初期化済みのマークを付けて、ContactFileUploaderによる二重初期化を防ぐ
      element.setAttribute('data-uploader-initialized', 'image');
    }
  });
}, { capture: true }); // capture: true で最初に実行
